// src/components/AddMemberDialog.tsx
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../contexts/NotificationContext';
import api from '../services/api';
import { addUserToEglise } from '../services/egliseService';

interface SearchedUser {
  id: number;
  name: string;
  email: string;
  phone?: string;
}

interface AddMemberDialogProps {
  open: boolean;
  onClose: () => void;
  egliseId: number;
  onSuccess: () => void;
}

const AddMemberDialog: React.FC<AddMemberDialogProps> = ({
  open,
  onClose,
  egliseId,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { error: showError, success: showSuccess } = useNotification();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchedUser[]>([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState<number | null>(null);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setSearching(true);
    setSearchResults([]);

    try {
      const response = await api.get<SearchedUser[]>('/users/search', {
        params: { email: searchQuery.trim() },
      });
      setSearchResults(response.data || []);
      if (response.data.length === 0) {
        showError(t('admin.users.noResults'));
      }
    } catch (err: any) {
      console.error('Failed to search users:', err);
      showError(err.response?.data?.detail || t('common.operationFailed'));
    } finally {
      setSearching(false);
    }
  };

  const handleAddMember = async (userId: number) => {
    setAdding(userId);

    try {
      await addUserToEglise(egliseId, userId);
      showSuccess(t('admin.users.addSuccess'));
      // Remove user from search results
      setSearchResults(prev => prev.filter(u => u.id !== userId));
      onSuccess();
    } catch (err: any) {
      console.error('Failed to add member:', err);
      if (err.response?.status === 409) {
        showError(t('admin.users.alreadyMember'));
      } else {
        showError(err.response?.data?.detail || t('admin.users.errorAdding'));
      }
    } finally {
      setAdding(null);
    }
  };

  const handleClose = () => {
    setSearchQuery('');
    setSearchResults([]);
    onClose();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('admin.users.addMemberTitle')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('admin.users.searchByEmail')}
        </Typography>

        <TextField
          fullWidth
          label={t('admin.users.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={searching}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleSearch} disabled={searching || !searchQuery.trim()}>
                  {searching ? <CircularProgress size={24} /> : <SearchIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Search Results */}
        {searchResults.length > 0 && (
          <List sx={{ mt: 2 }}>
            {searchResults.map((user) => (
              <ListItem key={user.id} divider>
                <ListItemAvatar>
                  <Avatar>{user.name.charAt(0).toUpperCase()}</Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={user.name}
                  secondary={user.email}
                />
                <ListItemSecondaryAction>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={adding === user.id ? <CircularProgress size={16} /> : <PersonAddIcon />}
                    onClick={() => handleAddMember(user.id)}
                    disabled={adding !== null}
                  >
                    {t('common.add')}
                  </Button>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddMemberDialog;
